import React, { useEffect, useState, useMemo, useRef } from "react";
import DashboardHeaderSection from "./dashboard-section/dashboard-header-section/dashboard-header-section";
import DashboardPlatformsSection from "./dashboard-section/dashboard-platforms-section/dashboard-platforms-section";
import { BreadcrumbComponent, AlertComponent } from "../../common/components";
import { useSetLoading } from "../../common/hooks/appearance";
import ChangePassword from "../change-password/change-password";
import { getDashboard } from "../../api/dashboard";
import { useLogin } from "../../common/hooks/useUser";
import "./dashboard.scss";
import Navbar from "./navbar/navbar";
import DashboardSubHeaderSection from "./dashboard-section/dashboard-header-section/dashboard-sub-header-section";
import MyDayChartSection from "./dashboard-section/dashboard-chart-section/my-day-chart-section";
import MyTaskChartSection from "./dashboard-section/dashboard-chart-section/my-task-chart-section";
import UpcomingBirthdayChartSection from "./dashboard-section/dashboard-chart-section/upcoming-birthday-chart-section";
import LeadsSourcesChartSection from "./dashboard-section/dashboard-chart-section/leads-sources-chart-section";
import MyCampaignsChartSection from "./dashboard-section/dashboard-chart-section/my-campaigns-chart-section";
import LeadStatusesChartSection from "./dashboard-section/dashboard-chart-section/lead-statuses-chart-section";
import NotesSection from "./dashboard-section/dashboard-chart-section/general-notes-section";
import dayjs, { Dayjs } from "dayjs";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import DashboardDrag, {
  COLS,
  compactLayoutAfterDelete,
} from "./widget/dashboard-drag";
import {
  addNewWidget,
  deleteWidgetItem,
  getDashboardWidget,
  getWidgetLibrary,
  updateDashboardExpend,
  updateDashboardLayout,
} from "../../api/widget";
import {
  DashboardInfoType,
  NewAddedWidgetType,
  UpdateChildLayoutType,
  UpdateLayoutType,
  WidgetConfig,
  WidgetElement,
  WidgetLibraryItem,
} from "../../utils/util-types";
import { LayoutItem } from "react-grid-layout";
import Upcoming from "./Upcoming";
import BusinessCardScanner from "./dashboard-section/dashboard-chart-section/business-card-scanner-section";
import { HIDE_WIDGETS } from "../../utils/constants";
import store from "../../store";
import MyActivitySection from "./dashboard-section/dashboard-chart-section/myActivitySection";

const Dashboard: React.FC = () => {
  const crumbMapping = [{ label: "Dashboard", link: "/home/dashboard" }];
  const tenantDetails = useLogin();
  const isShowUserLimitExceeded =
    tenantDetails?.userLimitExceeded &&
    localStorage.getItem("showFirstTimeLoginMsgs") === "true";

  const [isModalOpen, setIsModalOpen] = useState(
    localStorage.getItem("firstTimeLogin") === "true",
  );
  const [loading, setLoading] = useState(false);
  const [metricsData, setMetricsData] = useState([]);
  const [alertMessage, setAlertMessage] = useState<string | null>(
    "Users exceeds the limit allowed by your CRM plan. Please upgrade your plan or remove existing users to proceed.",
  );
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("error");
  const [alertOpen, setAlertOpen] = useState<boolean>(isShowUserLimitExceeded);
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [removeItems, setRemoveItemList] = useState<LayoutItem[]>([]);
  const [childItems, setChildItems] = useState<WidgetConfig[]>([]);
  const [dashboardInfo, setDashboardInfo] = useState<any>(null);

  const [widgetLibraryItemList, setWidgetLibraryItemList] = useState<
    WidgetLibraryItem[]
  >([]);
  const [childItemsPreviousState, setChildItemsPreviousState] = useState<
    WidgetConfig[]
  >([]);
  const [layoutItemsPreviousState, setLayoutItemsPreviousState] = useState<
    LayoutItem[]
  >([]);

  const [addedItemList, setAddedItemList] = useState<NewAddedWidgetType[]>([]);
  const [layoutItems, setLayoutItems] = useState<LayoutItem[]>([]);
  const [isLayoutUpdated, setLayoutUpdated] = useState<boolean>(false);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const response = await getDashboard();
      setMetricsData(response.data.data);
      setLoading(false);
    } catch (error: any) {
      setAlertMessage(
        error?.response?.data?.message || "Failed to fetch campaigns",
      );
      setAlertType("error");
      setAlertOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    getDashboardWidgets();
  }, [metricsData]);

  useEffect(() => {
    fetchLeads();
    getLibrary();
  }, []);

  const getChildren = (code?: string) => {
    switch (code) {
      case "MY_ACTIVITIES":
        return (
          <MyActivitySection
            setLoading={setLoading}
            setAlertOpen={setAlertOpen}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
          />
        );
      case "LEAD_STATUS":
        return <LeadStatusesChartSection metricsData={metricsData} />;
      case "NOTES_TO_CRM":
        return (
          <NotesSection
            setIsLoading={setLoading}
            setAlertOpen={setAlertOpen}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
          />
        );
      case "LEAD_SOURCE":
        return <LeadsSourcesChartSection metricsData={metricsData} />;
      case "MY_CAMPAIGNS":
        return <MyCampaignsChartSection metricsData={metricsData} />;
      case "UPCOMING_BIRTHDAYS":
        return <UpcomingBirthdayChartSection />;
      case "FIRST_CALL_RECORDER":
        return <Upcoming title="FIRST CALL RECORDER" />;
      case "REFERRAL_CARD_SCANNER":
        return <Upcoming title="REFERRAL CARD SCANNER" />;
      case "LEAD_QUALIFICATION_SCANNER":
        return <Upcoming title="LEAD QUALIFICATION SCANNER" />;
      case "BUSINESS_CARD_SCANNER":
        return (
          <BusinessCardScanner
            setLoading={setLoading}
            setAlertOpen={setAlertOpen}
            setAlertMessage={setAlertMessage}
            setAlertType={setAlertType}
          />
        );
    }
  };

  const prepareLibraryWidgets = (libraryItems: any) => {
    const idList: string[] = [];
    childItems?.forEach((item: WidgetConfig) => {
      if (!item.isDroppable) {
        // already added item
        idList.push(String(item.widgetId));
      }
    });
    const libraryList: WidgetLibraryItem[] = [];
    libraryItems.forEach((element: WidgetLibraryItem) => {
      if (!HIDE_WIDGETS.includes(element.code)) {
        // remove in future
        if (!idList.includes(String(element.id))) {
          libraryList.push({ ...element, isAdded: false });
        } else {
          libraryList.push({ ...element, isAdded: true });
        }
      }
    });
    setWidgetLibraryItemList(libraryList);
  };

  const getLibrary = async () => {
    try {
      const response: any = await getWidgetLibrary();
      if (response?.data?.success && response?.data?.data?.length > 0) {
        prepareLibraryWidgets(response?.data?.data);
      }
    } catch (e) {
      setWidgetLibraryItemList([]);
    }
  };
  const parseDashboardWidgetData = (widgets: WidgetElement[]) => {
    if (widgets?.length > 0) {
      const layoutItemList: LayoutItem[] = [];
      const childItemList: WidgetConfig[] = [];
      const childIdList: string[] = [];
      const time = new Date().getTime();
      widgets?.forEach((element: WidgetElement, index: number) => {
        if (!HIDE_WIDGETS.includes(element.widget.code)) {
          // remove in future
          const itemId = `widget-${time}-${element.id}`;

          layoutItemList.push({
            i: itemId,
            x: element.column_no - 1,
            y: element.row_no - 1,
            w: element.col_span,
            h: element.row_span,
            isResizable: false,
          });

          childItemList.push({
            id: itemId,
            dashboardWidgetId: element.id,
            widgetId: element?.widget.id,
            code: element?.widget?.code,
            child: getChildren(element?.widget?.code),
            isDroppable: false,
            title: element?.widget?.name,
            isNoteCRM: element?.widget?.code === "NOTES_TO_CRM",
            isScanner: element?.widget?.code === "BUSINESS_CARD_SCANNER",
          });

          childIdList.push(itemId);
        }
      });
      setChildItems(childItemList);
      setLayoutItems(layoutItemList);
      return { childList: childItemList, layoutList: layoutItemList };
    }
  };
  const getDashboardWidgets = async () => {
    try {
      const response: any = await getDashboardWidget();
      if (response?.data?.success) {
        setDashboardInfo(response?.data?.data);
        setIsExpanded(response?.data?.data?.is_expanded);
        if (response?.data?.data?.widgets?.length > 0) {
          parseDashboardWidgetData(response?.data?.data?.widgets);
        }
      }
    } catch (e) {
      setChildItems([]);
      setLayoutItems([]);
    }
  };

  useEffect(() => {
    if (widgetLibraryItemList?.length) {
      prepareLibraryWidgets(widgetLibraryItemList);
    }
  }, [childItems]);

  useEffect(() => {
    if (!localStorage.getItem("firstTimeLogin")) {
      setIsModalOpen(false);
    }
    if (localStorage.getItem("showFirstTimeLoginMsgs") === "true") {
      localStorage.setItem("showFirstTimeLoginMsgs", "false");
    }
  }, [isModalOpen]);

  const handleClose = () => {
    localStorage.removeItem("firstTimeLogin");
    setIsModalOpen(false);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  const updateExpended = async () => {
    const newExpended = !isExpanded;
    setIsExpanded(newExpended);
    const response = await updateDashboardExpend({ isExpanded: newExpended });
  };

  const getWidgetChildItem = (list: WidgetConfig[], id: string) => {
    return list?.filter((item: WidgetConfig) => item.id === id);
  };
  const getLayoutItem = (id: string) => {
    const index = layoutItems.findIndex((item) => item.i === id);
    if (index !== -1) {
      const item = layoutItems[index];
      return { layoutItem: item, index: index + 1 };
    }
    return null;
  };
  const saveDashboard = async () => {
    let updatedLayoutChildItems: UpdateChildLayoutType | undefined;
    if (addedItemList?.length > 0) {
      try {
        addedItemList.forEach(async (item: NewAddedWidgetType) => {
          const child: WidgetConfig = item.child;
          //const libraryItem: WidgetLibraryItem = item.libraryItem;
          //find layout item from layoutItem to get position
          const data = getLayoutItem(child.id);
          const index = data?.index;
          const searchedLayoutItem = data?.layoutItem;
          if (searchedLayoutItem) {
            const response = await addNewWidget({
              dashboardId: dashboardInfo?.dashboard_id,
              widgetId: child.widgetId,
              rowNo: searchedLayoutItem.y + 1,
              columnNo: searchedLayoutItem.x + 1,
              rowSpan: searchedLayoutItem.h,
              columnSpan: searchedLayoutItem.w,
              positionIndex: index,
            });
            if (
              response?.data?.success &&
              response?.data?.data?.data?.widgets?.length > 0
            ) {
              //find newly added item from response
              const newWidgetItem = response?.data?.data?.data?.widgets?.filter(
                (element: WidgetElement) =>
                  element?.widget.id === child.widgetId,
              );
              //update dashboard widget in in list
              if (newWidgetItem?.length > 0) {
                setChildItems((prev) =>
                  prev?.map((oldItem, i) =>
                    oldItem.widgetId === child.widgetId
                      ? { ...oldItem, dashboardWidgetId: newWidgetItem[0].id }
                      : oldItem,
                  ),
                );
              }

              // save as raw data
              setDashboardInfo((prev: any) => ({
                ...prev,
                widgets: response?.data?.data?.data?.widgets,
              }));
            }
          } else {
            console.log("Item not found");
          }
        });
        setAddedItemList([]);
      } catch (e) {}
    }
    // call remove API
    if (removeItems?.length > 0) {
      try {
        removeItems.forEach(async (item: LayoutItem) => {
          const idArray = item?.i?.split("-");
          await deleteWidgetItem(String(idArray[idArray?.length - 1]));
        });
        setRemoveItemList([]);
      } catch (e) {}
    }

    // call update layout API
    if (
      (updatedLayoutChildItems &&
        updatedLayoutChildItems?.layoutList?.length > 0) ||
      layoutItems?.length > 0
    ) {
      const updateItems: UpdateLayoutType[] = [];
      let layoutList: LayoutItem[] = [];
      let childList: WidgetConfig[] = [];
      if (
        updatedLayoutChildItems &&
        updatedLayoutChildItems?.layoutList?.length
      ) {
        layoutList = updatedLayoutChildItems?.layoutList;
        childList = updatedLayoutChildItems?.childList;
      } else {
        layoutList = layoutItems;
        childList = childItems;
      }

      layoutList.forEach((item: LayoutItem, index: number) => {
        const child = getWidgetChildItem(childList, item.i);

        if (child && child?.length > 0 && child[0]?.dashboardWidgetId) {
          updateItems.push({
            id: Number(child[0]?.dashboardWidgetId),
            rowNo: item.y + 1,
            columnNo: item.x + 1,
            rowSpan: item.h,
            columnSpan: item.w,
            positionIndex: index + 1,
          });
        }
      });
      if (updateItems?.length > 0) {
        await updateDashboardLayout({ widgets: updateItems });
      }
    }
    getLibrary();
    setEditMode(false);
    setLayoutUpdated(false);
  };

  const onNewWidgetAdded = (
    addedItem: WidgetConfig,
    activeItem: WidgetLibraryItem | null,
    index: number,
    newId: string,
    oldId: string,
  ) => {
    setAddedItemList((prev: any) => [
      ...prev,
      ...[{ child: addedItem, libraryItem: activeItem }],
    ]);
    setChildItems((prev) =>
      prev?.map((item, i) => (i === index ? addedItem : item)),
    );

    const newLayoutList = layoutItems.map((item: LayoutItem) =>
      item.i === oldId
        ? {
            ...item,
            i: newId,
            h: addedItem.code === "MY_ACTIVITIES" ? 3 : item.h,
          }
        : item,
    );
    setLayoutItems(compactLayoutAfterDelete(newLayoutList, COLS));
  };
  useEffect(() => {
    if (editMode) {
      setAddedItemList([]);
      setRemoveItemList([]);
      setLayoutItemsPreviousState([...layoutItems]);
      setChildItemsPreviousState([...childItems]);
    }
    store.dispatch({
      type: `DASHBOARD:SET_EDIT_MODE`,
      payload: {
        editMode,
      },
    });
  }, [editMode]);
  return (
    <div className="dashboard-container">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />
      <div className="dashboard-section">
        <DashboardHeaderSection />
        <Navbar />
        <Box>
          <DashboardSubHeaderSection
            isOpen={isExpanded}
            editMode={editMode}
            isSaveDisabled={
              removeItems?.length === 0 &&
              addedItemList?.length === 0 &&
              !isLayoutUpdated
            }
            setIsOpen={() => {
              updateExpended();
            }}
            onEditMode={() => {
              setEditMode(!editMode);
            }}
            saveWidget={saveDashboard}
            onCancelEdit={() => {
              setLayoutItems([...layoutItemsPreviousState]);
              setChildItems([...childItemsPreviousState]);
              setAddedItemList([]);
              setRemoveItemList([]);
              setLayoutItemsPreviousState([]);
              setChildItemsPreviousState([]);
              setEditMode(!editMode);
              setLayoutUpdated(false);
            }}
          />
          <DashboardDrag
            editMode={editMode}
            setRemoveItem={(removeItem) => {
              setRemoveItemList((prev: any) => [...prev, ...[removeItem]]);
            }}
            setAddedItem={onNewWidgetAdded}
            childItems={childItems || []}
            layoutItems={layoutItems}
            setChildItems={setChildItems}
            setLayoutItems={setLayoutItems}
            getChildren={getChildren}
            widgetLibraryItemList={widgetLibraryItemList}
            setLayoutUpdated={setLayoutUpdated}
          />
          <DashboardPlatformsSection />
        </Box>
      </div>
      <ChangePassword isOpen={isModalOpen} onClose={handleClose} />
      <AlertComponent
        message={alertMessage}
        type={alertType ?? "info"}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};
export default Dashboard;
